<template>
  <div>
    <RadioButtonMeasurementSwitcher
      v-if="canSwitchBmiMeasurementSystem && styleProps.measurementSwitcher == 'radio'"
      v-model="heightMeasurementSystem"
      :measurement-systems="measurementSystems"
      type="height"
    />
    <div class="flex flew-row">
      <ImperialHeightInput
        :heightInFeet.sync="heightInFeet"
        :heightInInches.sync="heightInInches"
        :input-style="styleProps.inputStyle"
        :measurement-indicators="styleProps.measurementIndicators.imperial"
        :disabled="disabled"
        @blur="$emit('blur')"
        v-if="
          heightMeasurementSystem === 'Imperial' ||
          heightMeasurementSystem === 'UsCustomary' ||
          heightMeasurementSystem === 'UsImperial'
        "
      />
      <MetricHeightInput
        :heightInCm.sync="heightInCm"
        :input-style="styleProps.inputStyle"
        :measurement-indicators="styleProps.measurementIndicators.metric"
        :disabled="disabled"
        @blur="$emit('blur')"
        v-if="heightMeasurementSystem === 'Metric'"
      />
    </div>
    <div v-if="canSwitchBmiMeasurementSystem && styleProps.measurementSwitcher == 'button'">
      <div v-if="heightMeasurementSystemOptions().includes('Metric')">
        <button class="mt-3 measurement-switcher" @click="switchHeightMeasurementSystem('Metric')" type="button">
          Change to centimetres
        </button>
      </div>
      <div v-if="showFeetInchesSwitch()">
        <button
          class="mt-3 measurement-switcher"
          @click="switchHeightMeasurementSystem(availableFeetInchesMeasurementSystem())"
          type="button"
        >
          Change to feet/inches
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import poster from "../../../mixins/poster";
import _ from "lodash";
import {
  HeightInputStyleProps,
  HeightMeasurement,
  ImperialHeightMeasurement,
  MeasurementSystem,
  MetricHeightMeasurement,
} from "../../../types";
import ImperialHeightInput from "./ImperialHeightInput.vue";
import MetricHeightInput from "./MetricHeightInput.vue";
import RadioButtonMeasurementSwitcher from "./RadioButtonMeasurementSwitcher.vue";

export interface HeightValueUpdatedEvent {
  isComplete: boolean;
  isValid: boolean;
  value: HeightMeasurement | null;
}

export default Vue.extend({
  components: {
    RadioButtonMeasurementSwitcher,
    ImperialHeightInput,
    MetricHeightInput,
  },
  mixins: [poster],
  props: {
    measurementSystems: { type: Array as PropType<MeasurementSystem[]>, required: true },
    initialValue: { type: Object as PropType<HeightMeasurement>, required: false },
    styleProps: { type: Object as PropType<HeightInputStyleProps>, required: true },
    disabled: { type: Boolean, default: false },
  },
  data() {
    let data = {
      heightInFeet: null,
      heightInInches: null,
      heightInCm: null,
      heightMeasurementSystem: this.measurementSystems[0],
      canSwitchBmiMeasurementSystem: this.measurementSystems.length > 1 && !this.disabled,
    };

    console.log("Initial value", this.initialValue);
    console.log("Measurement systems", this.measurementSystems);

    let isInitialValueMeasurementSystemAllowed = this.measurementSystems.includes(this.initialValue?.type);

    if (this.initialValue?.type === "Imperial") {
      // When the front-end sends weight data in UsImperial or UsCustomary, the back-ends returns it
      // as Imperial. At the same time, the allowed measurement system might still be UsCustomary.
      // This condition treats all three the same for initial value-ing purposes.
      isInitialValueMeasurementSystemAllowed = (["Imperial", "UsImperial", "UsCustomary"] as MeasurementSystem[]).some(
        (x) => this.measurementSystems.includes(x)
      );
    }

    if (!!this.initialValue?.type && isInitialValueMeasurementSystemAllowed) {
      // Accept the initial value only if we recognise the measurement system.
      data.heightMeasurementSystem = this.initialValue?.type;
      switch (this.initialValue?.type) {
        case "Imperial":
        case "UsImperial":
        case "UsCustomary":
          data.heightInFeet = this.initialValue.feet;
          data.heightInInches = this.initialValue.inches;
          break;
        case "Metric":
          data.heightInCm = this.initialValue.cm;
          break;
        default:
          if (!this.initialValue) {
            break;
          } else {
            console.error("Unrecognised measurement system " + this.initialValue.type);
            break;
          }
      }
    }

    return data;
  },
  created() {
    this.handleValueUpdate();
  },
  methods: {
    handleValueUpdate() {
      if (!this.isComplete || !this.isValid) {
        this.$emit("value-updated", {
          isComplete: this.isComplete,
          isValid: this.isValid,
          value: null,
        });
        return;
      }

      let height: HeightMeasurement;

      switch (this.heightMeasurementSystem) {
        case "Imperial":
        case "UsImperial":
        case "UsCustomary":
          height = this.imperialHeightMeasurement();
          break;
        case "Metric":
          height = this.metricHeightMeasurement();
          break;
        default:
          console.error("Unrecognised measurement system " + this.heightMeasurementSystem);
          break;
      }

      this.$emit("value-updated", {
        isComplete: this.isComplete,
        isValid: this.isValid,
        value: height,
      });
    },
    switchHeightMeasurementSystem(newSystem: MeasurementSystem) {
      if (!this.canSwitchBmiMeasurementSystem) {
        return;
      }
      this.heightMeasurementSystem = newSystem;
    },
    heightMeasurementSystemOptions() {
      return this.measurementSystems.filter((system) => system != this.heightMeasurementSystem);
    },
    imperialHeightMeasurement(): ImperialHeightMeasurement {
      return {
        type: "Imperial",
        feet: this.heightInFeet,
        inches: this.heightInInches,
      };
    },
    metricHeightMeasurement(): MetricHeightMeasurement {
      return {
        type: "Metric",
        cm: this.heightInCm,
      };
    },
    showFeetInchesSwitch(): boolean {
      return (
        (this.heightMeasurementSystemOptions().includes("Imperial") &&
          this.heightMeasurementSystem !== "UsImperial" &&
          this.heightMeasurementSystem !== "UsCustomary") ||
        (this.heightMeasurementSystemOptions().includes("UsImperial") &&
          this.heightMeasurementSystem !== "Imperial" &&
          this.heightMeasurementSystem !== "UsCustomary") ||
        (this.heightMeasurementSystemOptions().includes("UsCustomary") &&
          this.heightMeasurementSystem !== "UsImperial" &&
          this.heightMeasurementSystem !== "Imperial")
      );
    },
    availableFeetInchesMeasurementSystem(): MeasurementSystem {
      const feetInchesSystems = ["UsCustomary", "UsImperial", "Imperial"];
      return this.measurementSystems.find((system) => feetInchesSystems.includes(system)) || "Imperial";
    },
  },
  computed: {
    isComplete: function () {
      switch (this.heightMeasurementSystem) {
        case "Imperial":
        case "UsCustomary":
        case "UsImperial":
          return !!(_.isNumber(this.heightInFeet) && _.isNumber(this.heightInInches));
        default:
          return !!_.isNumber(this.heightInCm);
      }
    },
    isValid: function () {
      switch (this.heightMeasurementSystem) {
        case "Imperial":
        case "UsImperial":
        case "UsCustomary":
          return this.heightInFeet > 0 && this.heightInInches >= 0;
        default:
          return this.heightInCm > 0;
      }
    },
  },
  watch: {
    heightInFeet: function () {
      this.handleValueUpdate();
    },
    heightInInches: function () {
      this.handleValueUpdate();
    },
    heightInCm: function () {
      this.handleValueUpdate();
    },
    heightMeasurementSystem: function () {
      this.heightInCm = null;
      this.heightInFeet = null;
      this.heightInInches = null;
    },
  },
});
</script>
